import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits.
   * El botón "+" queda clicable (salvo server-disabled) para dar feedback del límite al intentar
   * superarlo; el bloqueo real lo hace `updateQuantity` (heredado).
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();

    if (!this.serverDisabledMinus) {
      minusButton.disabled = value <= min;
    }
    if (!this.serverDisabledPlus) {
      plusButton.disabled = false;
    }
  }

  /** @type {number | undefined} */
  #limitTimeout;

  /**
   * Feature: Límite máximo de compra — Mejora UX en el carrito.
   *
   * Si el cliente escribe una cantidad mayor al tope efectivo, el padre la "recorta" en silencio
   * y, además, dispara una actualización del carrito que hace `morphSection()` de toda la sección
   * (página o drawer), lo que borraría el mensaje recién mostrado.
   *
   * Aquí: detectamos el intento de exceder, recortamos al tope, mostramos el mensaje en el
   * contenedor de error de la propia fila (`.cart-items__error` / `.cart-item__error-text`,
   * presente en la página y en el drawer porque ambos usan snippets/cart-products.liquid) y
   * SOLO disparamos la actualización del carrito si la cantidad realmente cambió. Cuando el
   * cliente ya estaba en el máximo (caso típico del "quiero uno más"), evitamos el round-trip y
   * el morph, de modo que el mensaje PERSISTE — esto es lo que faltaba para que se viera en el
   * cart drawer.
   *
   * @param {Event} event - El evento blur del input.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const { quantityInput } = this.refs;
    const effectiveMax = this.getEffectiveMax();
    const { min } = this.getCurrentValues();
    const requested = parseInt(event.target.value, 10) || 0;
    const cartQuantity = parseInt(quantityInput.getAttribute('data-cart-quantity') || '0', 10);

    if (effectiveMax !== null && requested > effectiveMax) {
      const clamped = Math.max(min, effectiveMax);
      quantityInput.value = clamped.toString();
      this.updateButtonStates();
      this.showPurchaseLimitMessage();

      // Si el recorte no cambia la cantidad real del carrito → no actualizamos (sin morph → el
      // mensaje persiste). Si sí cambia (subió hasta el tope), notificamos el cambio.
      if (clamped !== cartQuantity) {
        this.onQuantityChange();
      }
      return;
    }

    super.setQuantity(event);
  }

  /**
   * Override: muestra el mensaje de límite en el contenedor de error de la propia fila del ítem
   * (`.cart-items__error` / `.cart-item__error-text`), presente en la página y en el cart drawer.
   */
  showPurchaseLimitMessage() {
    const message = this.dataset.plLimitMessage;
    if (!message) return;

    const row = this.closest('tr');
    const container = row?.querySelector('.cart-items__error');
    const textEl = row?.querySelector('.cart-item__error-text');
    if (!(container instanceof HTMLElement) || !(textEl instanceof HTMLElement)) return;

    textEl.textContent = message;
    container.classList.remove('hidden');

    if (this.#limitTimeout) clearTimeout(this.#limitTimeout);
    this.#limitTimeout = setTimeout(() => container.classList.add('hidden'), 6000);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
